import React, { useState, useEffect } from "react";
import {
  Modal,
  NumberInput,
  InlineNotification,
  Tile,
  Form,
  FormGroup,
  Tag,
  ContentSwitcher,
  Switch,
} from "@carbon/react";
import "./deductFuelModal.scss";
import { useTranslation } from "react-i18next";
import { queryClient } from "../..";
import fuelIcon from "../../assets/icons/fuel.svg";
import { useSetClientBalance } from "../../hooks/useSetClientBalance";

export default function DeductFuelModal({
  openModal,
  setOpenModal,
  editingClient,
  onSuccess,
}) {
  const { t } = useTranslation();
  const [balanceType, setBalanceType] = useState("fuel");
  const [fuel, setFuel] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [errorNotification, setErrorNotification] = useState({
    visibility: false,
    title: "",
    subtitle: "",
    kind: "",
  });

  const deductBalanceMutation = useSetClientBalance();

  const cost = Number(editingClient?.cost);

  const isValueValid =
    fuel !== "" && !isNaN(Number(fuel)) && Number(fuel) !== 0;

  const absFuel = Math.abs(Number(fuel));

  const fuelToDeduct = balanceType === "fuel" ? absFuel : absFuel / cost;

  const newBalance = Number(editingClient.fuel_balance) - fuelToDeduct;

  const isSubmitDisabled = submitting || !isValueValid;

  const handlePreventE = (evt) => {
    if (evt.key === "e") evt.preventDefault();
  };

  const onModalClose = () => {
    setFuel("");
    setSubmitting(false);
    setErrorNotification({
      visibility: false,
      title: "",
      subtitle: "",
      kind: "",
    });
    setOpenModal(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!isValueValid || submitting) return;

    setSubmitting(true);

    deductBalanceMutation.mutate(
      {
        guid: editingClient.guid,
        fuel: newBalance,
      },
      {
        onSuccess: () => {
          onSuccess?.();
          onModalClose();
          queryClient.invalidateQueries({ queryKey: ["getInfo"] });
          queryClient.invalidateQueries({ queryKey: ["chat_balance_history"] });
        },
        onError: () => {
          setErrorNotification({
            visibility: true,
            title: "authentication.notification.requestError500.title",
            subtitle: "authentication.notification.requestError500.serverError",
            kind: "error",
          });
          setSubmitting(false);
        },
      },
    );
  };

  return (
    <Modal
      open={!!openModal}
      onRequestClose={onModalClose}
      modalHeading={`${t("main.admin.deduct_fuel.modal.header")}`}
      modalLabel={`${t("main.chat.depositModal.label")}`}
      primaryButtonText={`${t("main.admin.deduct_fuel.modal.add")}`}
      secondaryButtonText={`${t("main.chat.addModal.cancel")}`}
      primaryButtonDisabled={isSubmitDisabled}
      onRequestSubmit={handleSubmit}
      onSecondarySubmit={onModalClose}
      closeButtonLabel={`${t("main.modalText.close")}`}
      className="deduct_fuel_modal"
    >
      <Tile className="deduct_fuel_modal_tile">
        <div className="deduct_fuel_modal_tile_client-info">
          <span>{`${t("main.admin.deduct_fuel.modal.title", { client: editingClient?.client })}`}</span>
          <span>{editingClient?.guid}</span>
        </div>
        <div className="deduct_fuel_modal_tile_balance-info">
          <span className="deduct_fuel_modal_tile_balance-info_balance">
            <img src={fuelIcon} alt="Fuel Icon" className="fuel_icon" />
            {editingClient?.fuel_balance}
          </span>
          <Tag size="lg" type="cyan">
            {`${t("main.admin.deduct_fuel.modal.costDesc", {
              cost: Number(
                (Number(editingClient?.cost) * 100).toPrecision(12),
              ).toString(),
            })}`}
          </Tag>
        </div>
      </Tile>

      <ContentSwitcher
        selectedIndex={balanceType === "fuel" ? 0 : 1}
        onChange={({ name }) => setBalanceType(name)}
        lowContrast
        className="deduct_fuel_modal_balance_switcher"
      >
        <Switch
          name="fuel"
          text={t("main.chat.chatClientsTable.balanceFuel")}
        />
        <Switch
          name="usd"
          disabled={!editingClient?.cost}
          text={t("main.chat.chatClientsTable.balanceUSD")}
        />
      </ContentSwitcher>

      <Form
        aria-label="sample form"
        className="deduct_fuel_modal_form"
        onSubmit={handleSubmit}
      >
        <FormGroup legendText="" className="deduct_fuel_modal_form_group">
          <NumberInput
            id="fuel"
            label={
              balanceType === "fuel"
                ? t("main.admin.deduct_fuel.modal.addFuel")
                : t("main.admin.deduct_fuel.modal.addBalance")
            }
            helperText={t("main.admin.deduct_fuel.modal.helperText")}
            value={fuel}
            min={-1000000}
            max={1000000}
            onChange={(evt, { value }) => setFuel(value)}
            onKeyDown={handlePreventE}
            allowEmpty
            hideSteppers
            invalid={isNaN(Number(fuel))}
            invalidText={t("main.chat.addModal.validationText.invalidValue")}
          />
        </FormGroup>
      </Form>
      {!editingClient?.cost && (
        <InlineNotification
          aria-label="notification"
          kind="warning"
          hideCloseButton
          onClose={() => {}}
          statusIconDescription="notification"
          subtitle={t("main.admin.deduct_fuel.modal.costNotificationSubtitle")}
          title={t("main.admin.deduct_fuel.modal.costNotificationTitle")}
        />
      )}

      {errorNotification.visibility && (
        <InlineNotification
          title={t(errorNotification.title)}
          subtitle={t(errorNotification.subtitle)}
          kind={errorNotification.kind}
          onClose={() =>
            setErrorNotification({
              visibility: false,
              title: "",
              subtitle: "",
              kind: "",
            })
          }
          lowContrast={true}
          aria-label={t("authentication.notification.closeNotification")}
          className="notification"
        />
      )}
    </Modal>
  );
}
